import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Experimental_CssVarsProvider as CssVarsProvider } from '@mui/material/styles';

import { themes } from './index';

const THEME_PRESET_STORAGE_KEY = 'arcmgr-theme-preset';
const DEFAULT_THEME_PRESET = 'classic';
const ThemePresetContext = createContext(null);

const isThemePreset = value => Object.prototype.hasOwnProperty.call(themes, value);

const readThemePreset = () => {
  try {
    const storedPreset = window.localStorage.getItem(THEME_PRESET_STORAGE_KEY);
    return isThemePreset(storedPreset) ? storedPreset : DEFAULT_THEME_PRESET;
  } catch {
    return DEFAULT_THEME_PRESET;
  }
};

export const ThemePresetProvider = ({children}) => {
  const [themePreset, setThemePresetState] = useState(readThemePreset);

  const setThemePreset = useCallback(nextPreset => {
    if (!isThemePreset(nextPreset)) return;

    setThemePresetState(nextPreset);
    try {
      window.localStorage.setItem(THEME_PRESET_STORAGE_KEY, nextPreset);
    } catch {
      // Keep the in-memory selection when storage is unavailable.
    }
  }, []);

  const value = useMemo(() => ({themePreset, setThemePreset}), [setThemePreset, themePreset]);

  return (
    <ThemePresetContext.Provider value={value}>
      <CssVarsProvider
        theme={themes[themePreset]}
        defaultMode="light"
        modeStorageKey="arcmgr-color-mode"
        colorSchemeStorageKey="arcmgr-color-scheme"
        disableTransitionOnChange
      >
        {children}
      </CssVarsProvider>
    </ThemePresetContext.Provider>
  );
};

ThemePresetProvider.propTypes = {
  children: PropTypes.node,
};

export const useThemePreset = () => {
  const value = useContext(ThemePresetContext);
  if (!value) throw new Error('useThemePreset must be used inside ThemePresetProvider');
  return value;
};
